<template>
  <UpdateBrandVoiceModal />
  <LayoutCard :custom-classes="'w-[840px]'">
     <template v-slot:header>
        <h1 class="text-lg font-medium text-[#3A4557]">Brand Settings</h1>
     </template>
     <template v-slot:body>
        <div class="flex flex-col gap-y-5">
           <!-- Brand Style -->
           <div>
              <h3 class="text-base font-medium mb-4 flex items-center">
                 Styles
                 <i
                    v-tooltip="'Define and manage your brand’s visual identity, ensuring consistency across all AI-generated posts.'"
                    class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
                    ></i>
              </h3>
              <div class="grid grid-cols-3 gap-4">
                 <div
                 v-for="style in AIUserProfile?.styles" :key="style?.id"
                 class="flex h-[58px] justify-between items-center p-2 rounded-[8px] text-[#4A4A4A]"
                 :style="{ backgroundColor: `${style?.colors?.brand_color}15` , borderColor: `${style?.colors?.brand_color}40` }"
                 >
                    <div class="flex flex-1 items-center gap-3">
                       <!-- Logo -->
                       <img v-if="style?.logo" :src="style?.logo" class="w-11 h-11 object-contain rounded-xl "  />
                       <img v-else src="@src/modules/publisher/assets/no-logo-placeholder.svg" class="w-10 h-10 rounded-xl "  />
                       <!-- Name -->
                      <div class="flex flex-col w-full gap-y-0.5">
                        <div
                          v-click-away="() => saveEdit(style?.id, 'styles')"
                          class="flex-1"
                          @click.stop
                          >
                          <p
                             v-if="!editingName[style?.id]"
                             v-tooltip="{ content: style?.name, placement: 'bottom' }"
                             class="text-sm truncate text-nowrap cursor-text pl-1 flex items-center gap-1 group"
                             @click="startEdit(style?.id)"
                             >
                             {{ truncateName('style', style?.name) }}
                             <i class="far fa-pencil text-gray-800 text-xs hidden group-hover:block"></i>
                          </p>

                          <input
                             v-else
                             :ref="(el) => (inputRefs[style?.id] = el)"
                             v-model="style.name"
                             class="w-full text-sm px-1 !outline-none !bg-transparent"
                             type="text"
                             @keyup.enter="saveEdit(style?.id, 'styles')"
                             @keyup.esc="cancelEdit(style?.id)"
                             />
                       </div>

                       <!-- Colors  -->
                        <div class="flex items-center gap-1">
                          <div
                             class="w-3 h-3 rounded-full border border-[#ECECEC]"
                             :style="{ backgroundColor: `${style?.colors?.brand_color}` }"
                             ></div>
                          <div
                             class="w-3 h-3 rounded-full border border-[#ECECEC]"
                             :style="{ backgroundColor: `${style?.colors?.background_color}` }"
                             ></div>
                          <div
                             class="w-3 h-3 rounded-full border border-[#ECECEC]"
                             :style="{ backgroundColor: `${style?.colors?.text_color}` }"
                             ></div>
                        </div>
                      </div>
                    </div>
                    <!-- Actions -->
                    <Dropdown
                       placement="right-start"
                       button-classes="border-none hover:!border-none !px-0 !gap-0 !bg-transparent"
                       dropdown-classes="!min-w-[9rem]"
                       @click.stop
                       >
                       <template v-slot:selected>
                          <i class="far fa-ellipsis-v cursor-pointer text-gray-800 p-2"></i>
                       </template>
                       <template v-slot:arrow>
                          <span></span>
                       </template>
                       <DropdownItem
                          size="sm"
                          class="flex items-center gap-2 !px-3 !py-2"
                          @click="handleUpdateStyle(style)"
                          >
                          <i class="far fa-pencil text-[#2294FF]"></i>
                          <span>Edit</span>
                       </DropdownItem>
                       <DropdownItem
                          size="sm"
                          class="flex items-center gap-2 !px-3 !py-2"
                          @click="handleDeleteStyle(style)"
                          >
                          <img src="@src/assets/img/icons/delete-colored.svg" alt="Delete" class="w-4 h-4">
                          <span>Delete</span>
                       </DropdownItem>
                    </Dropdown>
                 </div>
                 <!-- Add new style -->
                 <div
                    class="flex justify-center items-center gap-2 h-[58px] border rounded-[8px] !border-dashed text-[#4A4A4A] cursor-pointer"
                    @click="handleAddStyle"
                    >
                    <span
                       class="flex items-center justify-center rounded-full bg-gray-200 w-9 h-9"
                       >
                    <i class="far fa-plus font-normal text-lg" />
                    </span>
                    <p
                       class="font-medium text-center text-base text-gray-900"
                       >
                       Add New Style
                    </p>
                 </div>
              </div>
           </div>
           <!-- Brand Voice -->
           <div class="mt-4 mb-2">
              <h3 class="text-base font-medium mb-4 flex items-center">
                 Brand Voice
                 <i
                    v-tooltip="'Define and customize your brand’s communication style, including tone, audience, and key topics for AI-generated content.'"
                    class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
                    ></i>
              </h3>
              <div class="grid grid-cols-2 gap-4">
                 <div
                    v-for="voice in AIUserProfile?.brand_voices" :key="voice?.id"
                    class="flex flex-col  h-[160px] items-center justify-between p-3 rounded-[8px] text-[#4A4A4A]"
                    :style="{ backgroundColor: `${voice.color}15` , borderColor: `${voice.color}40` }"
                    >
                    <div class="flex item-center w-full px-2">
                       <div class="flex flex-1 items-center gap-2">
                          <!-- Logo -->
                          <div
                             class="h-9 w-9 p-[7px] rounded-full " :style="{ backgroundColor: `${voice.color}40` }"
                             >
                             <svg width="20" height="20" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                   fill-rule="evenodd" clip-rule="evenodd" d="M37.1348 1.78928C39.1067 1.98155 40.7773 3.56032 41.1747 5.62192C41.2431 5.96373 41.2559 6.32051 41.2559 6.66873C41.2602 15.2227 41.2623 23.7767 41.2559 32.3307C41.2516 35.2469 39.1195 37.3832 36.3059 37.2273C34.3618 37.1205 32.9924 36.0779 32.2233 34.2748C32.0823 33.9458 31.92 33.7941 31.5675 33.7258C30.532 33.5214 29.4987 33.3063 28.4676 33.0806H28.4612L27.3717 32.8456C26.1176 36.5629 23.5689 38.5091 19.6295 38.6522C19.6295 38.8701 19.6295 39.0881 19.6337 39.306C19.638 39.7888 19.6402 40.2673 19.6252 40.7459C19.5611 42.69 18.3562 44.3392 16.5937 44.9246C15.7217 45.2189 14.7798 45.2356 13.8979 44.9724C13.016 44.7091 12.2374 44.1789 11.6694 43.4548C10.9665 42.5661 10.7016 41.5363 10.7016 40.4147V36.9111C10.7016 34.5718 10.7016 32.2346 10.7144 29.8974C10.7166 29.4402 10.5927 29.2458 10.1376 29.1839C9.69234 29.1173 9.25036 29.0303 8.81306 28.9232C8.65943 28.8882 8.50561 28.854 8.35161 28.8207C7.50134 28.6348 7.0292 28.0666 7.01638 27.1906C7.01048 26.7064 7.00906 26.2222 7.01211 25.7379L7.01425 25.0393H4.37584C4.21915 25.0393 4.06247 25.0372 3.90584 25.0329C3.55902 25.0192 3.21171 25.0241 2.86543 25.0479C1.89338 25.1333 1.17984 24.7915 0.765381 23.8793V14.7271C1.15634 13.8256 1.83784 13.4987 2.80347 13.5436C3.6687 13.582 4.53606 13.5735 5.40343 13.5649C5.75379 13.5606 6.10202 13.5564 6.45024 13.5564C6.56133 13.5564 6.67029 13.5478 6.7942 13.5414L7.01638 13.5286V11.8836C7.02706 10.9265 7.44793 10.3839 8.38365 10.1852C12.3295 9.34346 16.2797 8.50601 20.2298 7.67069L25.2203 6.61319C27.3695 6.15814 29.5166 5.7031 31.6657 5.2566C31.9584 5.19678 32.0887 5.04937 32.1977 4.77378C32.9988 2.73142 34.9365 1.57137 37.1348 1.78928ZM3.9272 21.8583H6.97365V16.746H3.92506V21.8604L3.9272 21.8583ZM16.019 41.656C16.2984 41.3534 16.4631 40.9627 16.4847 40.5515C16.5125 38.0327 16.5082 35.5139 16.5061 32.993V32.9887L16.504 30.8759C16.5017 30.8058 16.4953 30.7359 16.4847 30.6665C16.4799 30.6253 16.4757 30.584 16.4719 30.5426L13.8335 29.9829V32.1983C13.8335 34.9008 13.8335 37.6033 13.8378 40.3036C13.8378 40.5472 13.8463 40.7993 13.9062 41.03C14.1518 41.9743 15.3439 42.3524 16.0212 41.6581L16.019 41.656ZM19.6551 31.2091V35.4135C21.6953 35.6143 23.7356 34.1744 24.2312 32.1791L22.5029 31.8137L19.6551 31.2091ZM17.6597 27.5602L31.8238 30.5597V8.43978L10.1761 13.0201V25.9793L17.6597 27.5602ZM38.1325 22.6936V7.6536C38.1368 7.2306 38.1411 6.80973 38.1218 6.38887C38.1069 5.99159 37.938 5.61569 37.651 5.34061C37.364 5.06554 36.9812 4.91283 36.5836 4.91478C35.6266 4.90837 34.9963 5.62192 34.9963 6.73069V32.3179C34.9963 32.4333 34.9942 32.5486 35.0092 32.6597C35.0312 32.8728 35.096 33.0793 35.1996 33.2668C35.3032 33.4543 35.4436 33.6189 35.6123 33.751C35.781 33.883 35.9746 33.9796 36.1815 34.0351C36.3884 34.0906 36.6044 34.1039 36.8165 34.074C37.6219 33.948 38.1304 33.2836 38.1304 32.3222L38.1325 22.6936Z"
                                   :fill="voice.color" />
                                <path
                                   d="M45.914 17.7101C48.1508 17.6845 50.3918 17.6888 52.6307 17.7059C53.4233 17.7101 53.8762 18.225 54.1625 18.9172V19.6542C53.7908 20.5686 53.1093 20.8805 52.1458 20.8591C50.941 20.8385 49.736 20.8328 48.531 20.842C47.6872 20.8442 46.8433 20.8484 46.0037 20.842C44.88 20.8314 44.1515 19.8379 44.4719 18.8061C44.5609 18.4917 44.7497 18.2148 45.0098 18.0171C45.2699 17.8194 45.5873 17.7117 45.914 17.7101ZM51.3638 32.654C50.3727 31.8036 49.3671 30.9703 48.3473 30.1544C48.14 30.0069 47.9136 29.8883 47.6743 29.8019C47.5648 29.7573 47.4565 29.7096 47.3496 29.6588C46.6532 29.7507 46.0806 30.1459 45.8542 30.7505C45.7308 31.0537 45.7079 31.3884 45.7886 31.7055C45.8693 32.0227 46.0495 32.3057 46.3028 32.513C47.3176 33.3889 48.3494 34.2434 49.3877 35.0916C50.1483 35.7133 51.0968 35.6385 51.6608 34.9356C52.2163 34.2456 52.1052 33.2799 51.3638 32.654ZM48.3858 8.38276L48.3323 8.42549C47.5932 9.02367 46.6724 8.92967 46.102 8.23535C45.5423 7.55599 45.6363 6.60958 46.3562 5.9943C47.3762 5.12456 48.406 4.2664 49.4454 3.41999C49.9624 2.99699 50.5435 2.91153 51.1502 3.21703C51.7142 3.50117 51.9834 3.98185 52.0133 4.87699C51.9813 4.93253 51.9471 5.00517 51.9065 5.08849C51.7899 5.36622 51.6225 5.61979 51.413 5.83621C50.4367 6.7228 49.4112 7.55171 48.3858 8.38062V8.38276Z"
                                   :fill="voice.color"/>
                             </svg>
                          </div>
                          <!-- Name -->
                          <div
                             v-click-away="() => saveEdit(voice?.id, 'brand_voices')"
                             class="flex-1"
                             @click.stop
                             >
                             <p
                                v-if="!editingName[voice?.id]"
                                v-tooltip="{ content: voice?.name, placement: 'bottom' }"
                                class="text-base font-medium truncate text-nowrap cursor-text pl-1 flex items-center gap-1 group"
                                @click="startEdit(voice?.id)"
                                >
                                {{ truncateName('voice', voice?.name) }}
                                <i class="far fa-pencil text-gray-800 text-xs hidden group-hover:block"></i>
                             </p>
                             <input
                                v-else
                                :ref="(el) => (inputRefs[voice?.id] = el)"
                                v-model="voice.name"
                                class="w-full text-sm px-1 !bg-transparent"
                                type="text"
                                @keyup.enter="saveEdit(voice?.id, 'brand_voices')"
                                @keyup.esc="cancelEdit(voice?.id)"
                                />
                          </div>
                       </div>
                       <!-- Actions -->
                       <Dropdown
                          placement="right-start"
                          button-classes="border-none hover:!border-none !px-0 !gap-0 !bg-transparent"
                          dropdown-classes="!min-w-[9rem]"
                          @click.stop
                          >
                          <template v-slot:selected>
                             <i class="fa fa-ellipsis-v cursor-pointer text-gray-800 font-normal p-2 rounded-md"></i>
                          </template>
                          <template v-slot:arrow>
                             <span></span>
                          </template>
                          <DropdownItem
                             size="sm"
                             class="flex items-center gap-2 !px-3 !py-2"
                             @click="handleUpdateBrandVoice(voice)"
                             >
                             <i class="far fa-pencil text-[#2294FF]"></i>
                             <span>Edit</span>
                          </DropdownItem>
                          <DropdownItem
                             size="sm"
                             class="flex items-center gap-2 !px-3 !py-2"
                             @click="handleDeleteBrandVoice(voice)"
                             >
                             <img src="@src/assets/img/icons/delete-colored.svg" alt="Delete" class="w-4 h-4">
                             <span>Delete</span>
                          </DropdownItem>
                       </Dropdown>
                    </div>
                    <!-- title -->
                    <div
                      v-click-away="() => saveTitleEdit(voice?.id)"
                      class="w-full px-2 mt-2"
                      @click.stop
                    >
                      <div
                        v-if="!editingTitle[voice?.id]"
                        class="flex items-start"
                        @click="startTitleEdit(voice?.id)"
                      >
                        <p class="text-sm text-gray-800 line-clamp-3 text-ellipsis cursor-text flex item-center group">
                          {{ voice?.title?.trim() || 'No Description Added' }}
                          <i class="far fa-pencil ml-1 text-gray-800 text-xs hidden group-hover:block"></i>
                        </p>

                      </div>
                      <div v-else class="relative">
                        <textarea
                          :ref="(el) => (titleInputRefs[voice?.id] = el)"
                          v-model="voice.title"
                          class="w-full text-sm !bg-transparent rounded resize-none no-scrollbar"
                          rows="2"
                          maxlength="100"
                          @keyup.enter="saveTitleEdit(voice?.id)"
                          @keyup.esc="cancelTitleEdit(voice?.id)"
                        ></textarea>
                      </div>
                    </div>

                    <!-- topics and updated date -->
                     <div class="flex w-full items-center justify-between mt-4 px-2">
                        <p class="text-xs px-2 py-1 font-medium rounded-xl" :style="{ backgroundColor: `${voice.color}30`, color: `${voice.color}` }">{{ voice?.topics?.length || 0 }} Topics</p>
                        <p class="text-xs text-gray-700">Edited {{  momentWrapper(moment.utc(voice?.updated_at)).formatTimezone().fromNow()}}</p>
                     </div>
                 </div>
                 <!-- Add new voice -->
                 <div
                    class="flex flex-col justify-center items-center gap-y-4  h-[160px] rounded-[8px] border !border-dashed text-[#4A4A4A] cursor-pointer"
                    @click="handleAddBrandVoice"
                    >
                    <span
                       class="flex items-center justify-center rounded-full bg-gray-200 w-9 h-9"
                       >
                    <i class="far fa-plus font-normal text-lg" />
                    </span>
                    <p
                       class="font-medium text-center text-base text-gray-900"
                       >
                       Add New Voice
                    </p>
                 </div>
              </div>
           </div>
        </div>
     </template>
  </LayoutCard>
  <CstConfirmationPopup
     modal-name="delete-style"
     primary-button-text="Delete"
     @confirmed="confirmDeleteStyle"
     >
     <template v-slot:head>
        <h4 class="text-xl !flex items-center gap-x-2"><img src="@src/assets/img/icons/delete-colored.svg" alt="Delete" class="w-5 h-5"> Delete Style</h4>
     </template>
     <template v-slot:body>
        <p class="pt-4">Are you sure you want to delete this style? This action cannot be undone.</p>
     </template>
     <template v-slot:footer="{ onCancel, onConfirm }">
        <Button
           variant="light"
           color="light"
           size="sm"
           @click="onCancel"
           >
        Cancel
        </Button>
        <Button
           color="danger"
           size="sm"
           @click="onConfirm"
           >
        Delete
        </Button>
     </template>
  </CstConfirmationPopup>
  <CstConfirmationPopup
     modal-name="delete-voice"
     primary-button-text="Delete"
     @confirmed="confirmDeleteVoice"
     >
     <template v-slot:head>
        <h4 class="text-xl !flex items-center gap-x-2"><img src="@src/assets/img/icons/delete-colored.svg" alt="Delete" class="w-5 h-5"> Delete Brand Voice</h4>
     </template>
     <template v-slot:body>
        <p class="pt-4">Are you sure you want to delete this brand voice? This action cannot be undone.</p>
     </template>
     <template v-slot:footer="{ onCancel, onConfirm }">
        <Button
           variant="light"
           color="light"
           size="sm"
           @click="onCancel"
           >
        Cancel
        </Button>
        <Button
           color="danger"
           size="sm"
           @click="onConfirm"
           >
        Delete
        </Button>
     </template>
  </CstConfirmationPopup>
</template>

<script setup>
import moment from 'moment'
  import { ref, nextTick, inject } from 'vue'
  import { Button, Dropdown, DropdownItem } from '@contentstudio/ui'
  import { useSetup } from '@modules/publisher/ai-content-library/composables/useSetup'
  import { useAIPostGeneration } from '@modules/publisher/ai-content-library/composables/useAIPostGeneration'
  import { useBrandInfoSetting } from '@modules/publisher/ai-content-library/composables/useBrandInfoSetting'
  import CstConfirmationPopup from '@ui/Popup/CstConfirmationPopup.vue'
  import useDateFormat from '@common/composables/useDateFormat'
  import UpdateBrandVoiceModal from './modals/UpdateBrandVoiceModal.vue'
  import LayoutCard from '@/src/modules/setting/components/white-label/LayoutCard.vue'
  import { useStore } from '@/src/store/base'

  const { momentWrapper } = useDateFormat()


  const root = inject('root')
  const { $cstuModal, $bvModal } = root
  const { dispatch } = useStore()

  const { setupInfo, AIUserProfile, brandInfo } = useSetup()
  const { updateStyleName, updateBrandVoiceTitle } = useAIPostGeneration(AIUserProfile)
  const { deleteStyle, updatePostGenerationSettings, deleteBrandVoice } = useBrandInfoSetting()


  const handleVoiceChange = (voiceId) => {
    updatePostGenerationSettings('brand_voice', voiceId)
  }

  const editingName = ref({})
  const inputRefs = ref({})
  const editingTitle = ref({})
  const titleInputRefs = ref({})
  const selectedStyle = ref(null)
  const selectedVoice = ref(null)
  const originalNames = ref({}) // Store original names to restore if cleared

  const truncateName = (type='style', name) => {
    if (type === 'style') {
      return name?.length > 16 ? `${name?.slice(0, 16)}...` : name
    } else {
      return name?.length > 20 ? `${name?.slice(0, 20)}...` : name
    }
  }

  // Function to check if a field is empty
const isFieldEmpty = (value) => {
    return !value || value.trim() === ''
  }

  const startEdit = async (id) => {
    editingName.value[id] = true

    // Store the original name before editing
    if (AIUserProfile.value.styles) {
      const style = AIUserProfile.value.styles.find((s) => s.id === id)
      if (style && style.name) {
        originalNames.value[id] = style.name
      }
    }

    if (AIUserProfile.value.brand_voices) {
      const voice = AIUserProfile.value.brand_voices.find((v) => v.id === id)
      if (voice && voice.name) {
        originalNames.value[id] = voice.name
      }
    }

    await nextTick()
    inputRefs.value[id]?.focus()
  }

  const saveEdit = (id, type = '') => {
    if (!editingName.value[id]) return
    editingName.value[id] = false

    // Here you can add API call to save the updated name
    if (type === 'styles') {
      const style = AIUserProfile.value.styles?.find((s) => s.id === id)

      // Check if name is empty or only whitespace
      if (isFieldEmpty(style.name)) {
        // Restore the original name
        if (originalNames.value[id]) {
          style.name = originalNames.value[id]
         //  dispatch('toastNotification', {
         //    message: 'Style name cannot be empty, restored to original',
         //    type: 'warning',
         //  })
        } else {
          style.name = 'Default Style'
          dispatch('toastNotification', {
            message: 'Style name cannot be empty',
            type: 'error',
          })
        }
        return
      }

      updateStyleName(type, id, style.name)
    } else if (type === 'brand_voices') {
      const voice = AIUserProfile.value.brand_voices?.find((s) => s.id === id)

      // Check if name is empty or only whitespace
      if (isFieldEmpty(voice.name)) {
        // Restore the original name
        if (originalNames.value[id]) {
          voice.name = originalNames.value[id]
         //  dispatch('toastNotification', {
         //    message: 'Brand voice name cannot be empty, restored to original',
         //    type: 'warning',
         //  })
        } else {
          voice.name = 'Default Voice'
          dispatch('toastNotification', {
            message: 'Brand voice name cannot be empty',
            type: 'error',
          })
        }
        return
      }

      updateStyleName(type, id, voice.name)
    }
  }

  const cancelEdit = (id) => {
    editingName.value[id] = false

    // Restore original name if it exists
    if (originalNames.value[id]) {
      // Check if it's a style or voice
      const style = AIUserProfile.value.styles?.find((s) => s.id === id)
      if (style) {
        style.name = originalNames.value[id]
      }

      const voice = AIUserProfile.value.brand_voices?.find((v) => v.id === id)
      if (voice) {
        voice.name = originalNames.value[id]
      }
    }
  }

  const startTitleEdit = async (id) => {
    editingTitle.value[id] = true

    // Store the original title before editing
    if (AIUserProfile.value.brand_voices) {
      const voice = AIUserProfile.value.brand_voices.find((v) => v.id === id)
      if (voice) {
        originalNames.value[`title_${id}`] = voice.title || ''
      }
    }

    await nextTick()
    titleInputRefs.value[id]?.focus()
  }

  const saveTitleEdit = (id) => {
    if (!editingTitle.value[id]) return
    editingTitle.value[id] = false

    const voice = AIUserProfile.value.brand_voices?.find((v) => v.id === id)
    if (voice) {
      // Validate title length before saving
      if (voice.title && voice.title.trim().length > 100) {
        dispatch('toastNotification', {
          message: 'Title must be less than 100 characters',
          type: 'error',
        })
        return
      }
      updateBrandVoiceTitle(id, voice.title.trim())
    }
  }

  const cancelTitleEdit = (id) => {
    editingTitle.value[id] = false

    // Restore original title if it exists
    if (originalNames.value[`title_${id}`] !== undefined) {
      const voice = AIUserProfile.value.brand_voices?.find((v) => v.id === id)
      if (voice) {
        voice.title = originalNames.value[`title_${id}`]
      }
    }
  }

  // Add new style
  const handleAddStyle = () => {
    $cstuModal.show('add-style-modal')
  }

  const handleAddBrandVoice = () => {
    $cstuModal.show('add-brand-voice-modal')
  }

  const handleUpdateStyle = (style) => {
    // set the setup info custom style
    setupInfo.custom = {
      id: style?.id,
      styleName: style?.name,
      logo: style?.logo,
      brandColor: style?.colors?.brand_color,
      backgroundColor: style?.colors?.background_color,
      textColor: style?.colors?.text_color,
      headingFont: style?.heading_font,
      bodyFont: style?.body_font
    }

    $cstuModal.show('update-style-modal')
  }

  const handleUpdateBrandVoice = (voice) => {
    console.log('handleUpdateBrandVoice',voice.strategy)

    // set the id
    brandInfo.brandVoiceId = voice?.id
    // Set brand info for update
    brandInfo.strategy = {
      id: voice?.strategy?.id,
      brandVoice: voice?.strategy?.voice || '',
      niche: voice?.strategy?.niche || '',
      audience: voice?.strategy?.audience || '',
      tone: Array.isArray(voice?.strategy?.tone) ? voice.strategy.tone : [],
      language: Array.isArray(voice?.strategy?.language) ? voice.strategy.language : [],
      emotion: Array.isArray(voice?.strategy?.emotion) ? voice.strategy.emotion : [],
      competitors: Array.isArray(voice?.strategy?.competitors) ? voice.strategy.competitors : [],
    }
    brandInfo.topics = voice?.topics || []
    brandInfo.name = voice?.name
    brandInfo.title = voice?.title
    brandInfo.color = voice?.color

    // Show the modal in update mode
    $cstuModal.show('update-brand-voice-modal')
  }

  const handleDeleteStyle = (style)=>{
    selectedStyle.value = style
    $bvModal.show('delete-style-confirmation-modal')
  }

  const confirmDeleteStyle = () => {
    deleteStyle(selectedStyle.value.id)

    $bvModal.hide('delete-style-confirmation-modal')
    selectedStyle.value = null
  }

  const handleDeleteBrandVoice = (voice) => {
    selectedVoice.value = voice
    $bvModal.show('delete-voice-confirmation-modal')
  }

  const confirmDeleteVoice = () => {
    deleteBrandVoice(selectedVoice.value.id)

    $bvModal.hide('delete-voice-confirmation-modal')
    selectedVoice.value = null
  }
</script>

<style scoped>
  .bg-preview {
  background: linear-gradient(
  to bottom,
  rgba(0, 0, 0, 0.1) -26%,
  rgba(0, 0, 0, 0.3) 100%
  ) !important;
  }
</style>
